import { FormEvent, useEffect, useMemo, useState } from "react";
import { Button, Form, Spinner } from "reactstrap";
import { toast } from "react-toastify";
import api from "../../../../Hooks/axiosApi";
import { useSafeNavigate } from "../../../../Hooks/useSafeNavigate";
import {
  LoanProductForm,
  Lender,
  buildLoanDetailsJsonFromSelection,
  buildLoanProductPayload,
  createDefaultLoanDocumentationSelection,
  createDefaultPropertyDetails,
  createDefaultAssetSecuredDetails,
  createDefaultAssetUnsecuredDetails,
  defaultLoanProductForm,
  loanDocumentationRows,
  normalizeLender,
  productTypeOptions,
  PropertyDetails,
  AssetSecuredDetails,
  AssetUnsecuredDetails,
  validateLoanProductForm,
  LoanDocumentationSelection,
} from "./loanProductUtils";
import PropertyProductTypeFields from "./PropertyProductTypeFields";
import AssetSecuredProductTypeFields from "./AssetSecuredProductTypeFields";
import AssetUnsecuredProductTypeFields from "./AssetUnsecuredProductTypeFields";

const AddCRMLoanProduct = () => {
  const navigate = useSafeNavigate();
  const [form, setForm] = useState<LoanProductForm>(defaultLoanProductForm);
  const [lenders, setLenders] = useState<Lender[]>([]);
  const [documentationSelection, setDocumentationSelection] = useState<LoanDocumentationSelection>(
    createDefaultLoanDocumentationSelection()
  );
  const [propertyDetails, setPropertyDetails] = useState<PropertyDetails>(createDefaultPropertyDetails());
  const [assetSecuredDetails, setAssetSecuredDetails] = useState<AssetSecuredDetails>(createDefaultAssetSecuredDetails());
  const [assetUnsecuredDetails, setAssetUnsecuredDetails] = useState<AssetUnsecuredDetails>(
    createDefaultAssetUnsecuredDetails()
  );
  const [errors, setErrors] = useState<Partial<Record<keyof LoanProductForm, string>>>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [apiError, setApiError] = useState<string>("");



  useEffect(() => {
    const fetchLenders = async () => {
      setLoading(true);
      setApiError("");
      try {
        const response = await api.get("/api/Lenders/Getlenders");
        const lendersData = Array.isArray(response?.data) ? response.data : [];
        const normalized = lendersData.map(normalizeLender).filter((item: Lender) => item.lenderId > 0);
        normalized.sort((a: Lender, b: Lender) => (a.lenderName || "").localeCompare(b.lenderName || ""));
        setLenders(normalized);
      } catch (error: any) {
        setApiError(error?.response?.data?.message || "Failed to load lenders.");
      } finally {
        setLoading(false);
      }
    };

    fetchLenders();
  }, []);

  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      loanDetailsJson: buildLoanDetailsJsonFromSelection(
        documentationSelection,
        prev.productType,
        propertyDetails,
        assetSecuredDetails,
        assetUnsecuredDetails
      ),
    }));
  }, [documentationSelection, propertyDetails, assetSecuredDetails, assetUnsecuredDetails]);

  const handleChange = (name: keyof LoanProductForm, value: string) => {
    let nextValue = value;

    if (name === "productName") {
      nextValue = value.slice(0, 100);
    }
    setForm((prev) => {
      const nextForm = { ...prev, [name]: nextValue };
      nextForm.loanDetailsJson = buildLoanDetailsJsonFromSelection(
        documentationSelection,
        nextForm.productType,
        propertyDetails,
        assetSecuredDetails,
        assetUnsecuredDetails
      );
      return nextForm;
    });

    setErrors((prev) => ({ ...prev, [name]: "" }));
    setApiError("");
  };



  const createLoanProduct = async (payload: any) => {
    try {
      return await api.post("/api/Lenders/Insertloan-products", payload);
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 404 || status === 405) {
        return api.post("/api/Lenders/Postloan-products", payload);
      }
      throw error;
    }
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const validationErrors = validateLoanProductForm(form);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      toast.error("Please correct validation errors.");
      return;
    }

    setSubmitting(true);
    setApiError("");

    const payload = buildLoanProductPayload(form);

    try {
      await createLoanProduct(payload);
      toast.success("Loan product added successfully.");
      navigate("/CRMLoanProducts");
    } catch (error: any) {
      const message = error?.response?.data?.message || "Failed to add loan product.";
      setApiError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-4">
        <Spinner color="primary" />
      </div>
    );
  }

  return (
    <div className="px-4 py-4" style={{ minHeight: "300px", boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)", backgroundColor: "#f9f9f9", borderRadius: "8px" }}>
      <Form onSubmit={handleSubmit} style={{ boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)", backgroundColor: "white", borderRadius: "8px", padding: "20px" }}>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h3 className="f-w-900 m-0 text-primary">Add Loan Product</h3>
        </div>

        {!!apiError && <div className="alert alert-danger py-2">{apiError}</div>}

        <div className="row g-3">
          <div className="col-md-3">
            <label className="form-label">Lender *</label>
            <select className={`form-control ${errors.lenderId ? "is-invalid" : ""}`} value={form.lenderId} onChange={(e) => handleChange("lenderId", e.target.value)}>
              <option value="">Select Lender</option>
              {lenders.map((lender) => (
                <option key={lender.lenderId} value={lender.lenderId}>{lender.lenderName}</option>
              ))}
            </select>
            {errors.lenderId && <div className="invalid-feedback">{errors.lenderId}</div>}
          </div>

          <div className="col-md-3">
            <label className="form-label">Product Name *</label>
            <input className={`form-control ${errors.productName ? "is-invalid" : ""}`} type="text" maxLength={100} value={form.productName} onChange={(e) => handleChange("productName", e.target.value)} />
            {errors.productName && <div className="invalid-feedback">{errors.productName}</div>}
          </div>

          <div className="col-md-3">
            <label className="form-label">Product Type *</label>
            <select className={`form-control ${errors.productType ? "is-invalid" : ""}`} value={form.productType} onChange={(e) => handleChange("productType", e.target.value)}>
              <option value="">Select Product Type</option>
              {productTypeOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            {errors.productType && <div className="invalid-feedback">{errors.productType}</div>}
          </div>


          {form.productType === "Property" && <PropertyProductTypeFields details={propertyDetails} onChange={setPropertyDetails} />}
          {form.productType === "Asset - Secured" && (
            <AssetSecuredProductTypeFields details={assetSecuredDetails} onChange={setAssetSecuredDetails} />
          )}
          {form.productType === "Asset - Unsecured" && (
            <AssetUnsecuredProductTypeFields details={assetUnsecuredDetails} onChange={setAssetUnsecuredDetails} />
          )}



        </div>

        <div className="d-flex gap-2 mt-4">
          <Button type="button" color="secondary" onClick={() => navigate("/CRMLoanProducts")}>Back to Loan Products</Button>
          <Button type="submit" color="primary" disabled={submitting}>{submitting ? "Saving..." : "Save Loan Product"}</Button>
        </div>
      </Form>
    </div>
  );
};

export default AddCRMLoanProduct;







